import { useEffect, useRef, useState } from "react";
import {
  CalendarClock,
  Check,
  FileImage,
  ImagePlus,
  Plus,
  ShieldCheck,
  X,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  createQuickOrder,
  uploadQuickOrderMaterial,
} from "../../entities/order/api";
import { getApiErrorMessage } from "../../shared/api/errors";
import { showToast } from "../../shared/ui/toast";
import "./NewOrderPage.css";

const INITIAL_FORM = {
  title: "",
  amount: "",
  usageScenario: "",
  requirement: "",
  deliverable: "",
  acceptance: "",
  dispatchType: "INSTANT",
  schedule: "",
  revisionRounds: "2",
  containsThirdPartyAssets: false,
};

export default function NewOrderPage() {
  const navigate = useNavigate();
  const materialFilesRef = useRef([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [materialFiles, setMaterialFiles] = useState([]);
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    materialFilesRef.current = materialFiles;
  }, [materialFiles]);

  useEffect(
    () => () => {
      materialFilesRef.current.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    },
    [],
  );

  function updateField(field, value) {
    setError("");
    setForm((current) => ({ ...current, [field]: value }));
  }

  function chooseMaterials(event) {
    const selected = Array.from(event.target.files || []);
    const additions = selected
      .filter((file) => file.type.startsWith("image/") || file.type.startsWith("video/"))
      .filter((file) => file.size > 0)
      .slice(0, Math.max(0, 6 - materialFiles.length))
      .map((file) => ({
        id: crypto.randomUUID(),
        file,
        previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
      }));
    setMaterialFiles((current) => [...current, ...additions]);
    event.target.value = "";
  }

  function removeMaterial(materialId) {
    setMaterialFiles((current) => {
      const target = current.find((item) => item.id === materialId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return current.filter((item) => item.id !== materialId);
    });
  }

  async function uploadMaterials() {
    const assets = [];
    for (let index = 0; index < materialFiles.length; index += 1) {
      const material = materialFiles[index];
      const asset = await uploadQuickOrderMaterial(material.file, (fileProgress) => {
        setUploadProgress(Math.round(
          ((index + fileProgress / 100) / materialFiles.length) * 100,
        ));
      });
      assets.push(asset);
    }
    return assets;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    if (!form.title.trim() || !form.amount || Number(form.amount) <= 0) {
      setError("请填写需求标题和有效预算");
      return;
    }
    if (!form.requirement.trim()) {
      setError("请描述具体的设计需求");
      return;
    }
    if (!materialFiles.length) {
      setError("请至少添加一份参考素材");
      return;
    }

    setError("");
    setSubmitting(true);
    setUploadProgress(0);
    try {
      const assets = await uploadMaterials();
      const result = await createQuickOrder({
        title: form.title.trim(),
        amount_yuan: Number(form.amount).toFixed(2),
        requirement_desc: form.requirement.trim(),
        usage_scenario: form.usageScenario.trim(),
        deliverable_desc: form.deliverable.trim(),
        acceptance_criteria: form.acceptance.trim(),
        dispatch_type: form.dispatchType,
        revision_rounds: Number(form.revisionRounds),
        asset_ids: assets.map((asset) => String(asset.id)),
        reservation_snapshot: {
          materials_ready: true,
          expected_draft_label: form.schedule.trim(),
        },
        protection_snapshot: {
          risk_level: "STANDARD",
          copyright_owner: "需求方",
          usage_license_scope: "仅限本订单约定用途",
          contains_third_party_assets: form.containsThirdPartyAssets,
        },
      });
      setSubmittedOrder(result.order);
      showToast("设计需求发布成功");
    } catch (requestError) {
      const message = requestError.response
        ? getApiErrorMessage(requestError, "需求发布失败，请稍后重试")
        : requestError.message || "需求发布失败，请稍后重试";
      setError(message);
      showToast(message, "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="page-container narrow-page order-create-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">设计需求</span>
          <h1>把想法交给合适的设计师</h1>
          <p>清晰说明预算、交付物和验收标准，减少反复沟通。</p>
        </div>
      </div>

      <form className="order-composer-card" onSubmit={handleSubmit}>
        {submittedOrder ? (
          <div className="success-state">
            <span><Check size={30} /></span>
            <h2>需求已创建</h2>
            <p>订单号 {submittedOrder.order_no}，提交审核后即可进入后续交易流程。</p>
            <div className="order-success-actions">
              <button
                className="secondary-button"
                type="button"
                onClick={() => navigate("/orders?view=sent")}
              >
                查看发单列表
              </button>
              <button
                className="primary-button"
                type="button"
                onClick={() => navigate(`/orders/${submittedOrder.id}`)}
              >
                查看订单详情
              </button>
            </div>
          </div>
        ) : (
          <>
            <section className="order-form-section" aria-labelledby="order-basic-title">
              <div className="order-form-section-heading">
                <span>01</span>
                <div>
                  <h2 id="order-basic-title">基础信息</h2>
                  <p>一句话说明你需要什么，以及计划投入的预算。</p>
                </div>
              </div>
              <div className="order-form-grid">
                <label className="order-field wide">
                  <span>需求标题</span>
                  <input
                    required
                    value={form.title}
                    maxLength={128}
                    onChange={(event) => updateField("title", event.target.value)}
                    placeholder="例如：咖啡品牌夏季上新系列海报"
                  />
                  <small>{form.title.length}/128</small>
                </label>
                <label className="order-field">
                  <span>预算金额</span>
                  <span className="order-money-input">
                    <b>¥</b>
                    <input
                      required
                      inputMode="decimal"
                      value={form.amount}
                      onChange={(event) => updateField("amount", event.target.value)}
                      placeholder="0.00"
                    />
                  </span>
                </label>
                <label className="order-field">
                  <span>使用场景（选填）</span>
                  <input
                    value={form.usageScenario}
                    maxLength={128}
                    onChange={(event) => updateField("usageScenario", event.target.value)}
                    placeholder="电商首页、线下门店、社交媒体"
                  />
                </label>
                <label className="order-field wide">
                  <span>需求描述</span>
                  <textarea
                    required
                    value={form.requirement}
                    onChange={(event) => updateField("requirement", event.target.value)}
                    placeholder="说明品牌背景、画面风格、尺寸、文案和必须保留的元素……"
                  />
                </label>
              </div>
            </section>

            <section className="order-form-section" aria-labelledby="order-delivery-title">
              <div className="order-form-section-heading">
                <span>02</span>
                <div>
                  <h2 id="order-delivery-title">交付与验收</h2>
                  <p>提前约定交付范围，双方协作会更顺畅。</p>
                </div>
              </div>
              <div className="order-form-grid">
                <label className="order-field">
                  <span>期望交付物（选填）</span>
                  <textarea
                    value={form.deliverable}
                    onChange={(event) => updateField("deliverable", event.target.value)}
                    placeholder="例如：3 张主视觉海报及可编辑源文件"
                  />
                </label>
                <label className="order-field">
                  <span>验收标准（选填）</span>
                  <textarea
                    value={form.acceptance}
                    onChange={(event) => updateField("acceptance", event.target.value)}
                    placeholder="例如：尺寸、格式和品牌规范均符合需求"
                  />
                </label>
                <label className="order-field">
                  <span>期望初稿时间（选填）</span>
                  <input
                    value={form.schedule}
                    maxLength={64}
                    onChange={(event) => updateField("schedule", event.target.value)}
                    placeholder="例如：确认接单后 3 天"
                  />
                </label>
                <label className="order-field">
                  <span>包含修改轮次</span>
                  <select
                    value={form.revisionRounds}
                    onChange={(event) => updateField("revisionRounds", event.target.value)}
                  >
                    <option value="0">不含修改</option>
                    <option value="1">1 轮</option>
                    <option value="2">2 轮</option>
                    <option value="3">3 轮</option>
                    <option value="5">5 轮</option>
                  </select>
                </label>
              </div>
              <div className="order-dispatch-options" aria-label="需求类型">
                <button
                  className={form.dispatchType === "INSTANT" ? "active" : ""}
                  type="button"
                  onClick={() => updateField("dispatchType", "INSTANT")}
                >
                  <Zap size={20} />
                  <span><strong>即时需求</strong><small>审核与支付后尽快开放接单</small></span>
                </button>
                <button
                  className={form.dispatchType === "RESERVATION" ? "active" : ""}
                  type="button"
                  onClick={() => updateField("dispatchType", "RESERVATION")}
                >
                  <CalendarClock size={20} />
                  <span><strong>预约需求</strong><small>按约定时间推进设计服务</small></span>
                </button>
              </div>
            </section>

            <section className="order-form-section" aria-labelledby="order-material-title">
              <div className="order-form-section-heading">
                <span>03</span>
                <div>
                  <h2 id="order-material-title">参考素材</h2>
                  <p>素材将进入交易私有存储，仅用于本次订单。</p>
                </div>
              </div>
              <label className="order-material-upload">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                  multiple
                  onChange={chooseMaterials}
                />
                <ImagePlus size={23} />
                <span><strong>添加图片或视频</strong><small>最多 6 个文件，图片不超过 10 MB，视频不超过 100 MB</small></span>
                <Plus size={18} />
              </label>
              {materialFiles.length ? (
                <div className="order-material-list">
                  {materialFiles.map((material) => (
                    <article key={material.id}>
                      {material.previewUrl ? (
                        <img src={material.previewUrl} alt="" />
                      ) : (
                        <span className="order-material-file-icon"><FileImage size={21} /></span>
                      )}
                      <span>
                        <strong>{material.file.name}</strong>
                        <small>{(material.file.size / 1024 / 1024).toFixed(2)} MB</small>
                      </span>
                      <button
                        type="button"
                        aria-label={`移除 ${material.file.name}`}
                        onClick={() => removeMaterial(material.id)}
                      >
                        <X size={16} />
                      </button>
                    </article>
                  ))}
                </div>
              ) : null}
              <label className="order-third-party-check">
                <input
                  type="checkbox"
                  checked={form.containsThirdPartyAssets}
                  onChange={(event) => updateField(
                    "containsThirdPartyAssets",
                    event.target.checked,
                  )}
                />
                <ShieldCheck size={17} />
                <span>素材中包含第三方字体、图片或其他授权资源</span>
              </label>
            </section>

            {submitting ? (
              <div className="order-upload-progress">
                <span style={{ width: `${uploadProgress}%` }} />
                <b>{uploadProgress < 100 ? `正在处理素材 ${uploadProgress}%` : "正在创建订单……"}</b>
              </div>
            ) : null}
            {error ? <p className="form-error">{error}</p> : null}
            <div className="composer-footer">
              <button className="ghost-button" type="button" onClick={() => navigate("/create")}>
                取消
              </button>
              <button className="primary-button" type="submit" disabled={submitting}>
                {submitting ? "发布中……" : "发布设计需求"}
              </button>
            </div>
          </>
        )}
      </form>
    </section>
  );
}
